import React from "react";
import { useNavigate } from "react-router-dom";
import { reportsApi } from "../../api/reportsApi";
import { useAuth } from "../../auth/authStore";
import { useToast } from "../../ui/toast/ToastProvider";

const ReportContentContext = React.createContext(null);

const REASONS = [
  { code: "SPAM", label: "Спам или реклама" },
  { code: "ABUSE", label: "Оскорбления, травля или токсичность" },
  { code: "HATE_OR_DISCRIMINATION", label: "Дискриминация или язык вражды" },
  { code: "SEXUAL_OR_VIOLENT", label: "Неприемлемый контент" },
  { code: "DANGEROUS_ADVICE", label: "Опасные советы" },
  { code: "MISINFORMATION", label: "Недостоверная информация" },
  { code: "PRIVACY", label: "Раскрытие личных данных" },
  { code: "COPYRIGHT", label: "Нарушение авторских прав" },
  { code: "OTHER", label: "Другое" },
];

function safeTrim(value, max = 500) {
  return String(value || "").trim().slice(0, max);
}

function getCurrentUrl() {
  if (typeof window === "undefined") return "";
  return String(window.location.href || "");
}

export function ReportContentProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [target, setTarget] = React.useState(null);
  const [reasonCode, setReasonCode] = React.useState(REASONS[0].code);
  const [details, setDetails] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const closeReport = React.useCallback(() => {
    if (submitting) return;
    setTarget(null);
    setReasonCode(REASONS[0].code);
    setDetails("");
  }, [submitting]);

  const openReport = React.useCallback(
    (nextTarget = {}) => {
      const targetType = safeTrim(nextTarget.targetType, 80).toUpperCase();
      const targetId = safeTrim(nextTarget.targetId, 120);
      if (!targetType || !targetId) {
        toast.error("Не удалось определить контент для жалобы");
        return;
      }

      if (!isAuthenticated) {
        toast.info("Чтобы отправить жалобу, войдите в аккаунт");
        navigate("/login");
        return;
      }

      setTarget({
        targetType,
        targetId,
        title: safeTrim(nextTarget.title, 180),
        contentSnippet: safeTrim(nextTarget.contentSnippet, 800),
        url: safeTrim(nextTarget.url || getCurrentUrl(), 1000),
        meta: nextTarget.meta && typeof nextTarget.meta === "object" ? nextTarget.meta : {},
      });
      setReasonCode(REASONS[0].code);
      setDetails("");
    },
    [isAuthenticated, navigate, toast]
  );

  const submitReport = React.useCallback(
    async (event) => {
      event?.preventDefault?.();
      if (!target || submitting) return;

      const reason = REASONS.find((item) => item.code === reasonCode) || REASONS[REASONS.length - 1];
      const cleanDetails = safeTrim(details, 1000);
      if (reason.code === "OTHER" && cleanDetails.length < 5) {
        toast.info("Кратко опишите причину жалобы");
        return;
      }

      setSubmitting(true);
      try {
        await reportsApi.submitReport({
          ...target,
          reason: reason.code,
          reasonCode: reason.code,
          reasonText: reason.label,
          details: cleanDetails,
        });
        toast.success("Жалоба отправлена на модерацию");
        setTarget(null);
        setReasonCode(REASONS[0].code);
        setDetails("");
      } catch (error) {
        toast.error(error?.message || "Не удалось отправить жалобу");
      } finally {
        setSubmitting(false);
      }
    },
    [details, reasonCode, submitting, target, toast]
  );

  React.useEffect(() => {
    if (!target) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeReport();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeReport, target]);

  const contextValue = React.useMemo(() => ({ openReport }), [openReport]);

  return (
    <ReportContentContext.Provider value={contextValue}>
      {children}
      <div
        className={`report-modal-overlay${target ? " open" : ""}`}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeReport();
        }}
        aria-hidden={!target}
      >
        {target ? (
          <form className="report-modal" role="dialog" aria-modal="true" aria-label="Жалоба на контент" onSubmit={submitReport}>
            <div className="report-modal__header">
              <div>
                <div className="report-modal__eyebrow">Модерация</div>
                <h3>Пожаловаться на контент</h3>
              </div>
              <button type="button" className="report-modal__close" onClick={closeReport} aria-label="Закрыть" disabled={submitting}>
                ✕
              </button>
            </div>

            {target.title || target.contentSnippet ? (
              <div className="report-modal__target">
                {target.title ? <strong>{target.title}</strong> : null}
                {target.contentSnippet ? <span>{target.contentSnippet}</span> : null}
              </div>
            ) : null}

            <label className="report-field">
              <span>Причина</span>
              <select value={reasonCode} onChange={(event) => setReasonCode(event.target.value)} disabled={submitting}>
                {REASONS.map((reason) => (
                  <option key={reason.code} value={reason.code}>{reason.label}</option>
                ))}
              </select>
            </label>

            <label className="report-field">
              <span>Комментарий модератору</span>
              <textarea
                value={details}
                onChange={(event) => setDetails(event.target.value)}
                placeholder="Опишите, что не так с этим материалом. Это поможет быстрее проверить жалобу."
                rows={5}
                maxLength={1000}
                disabled={submitting}
              />
              <small>{details.length}/1000</small>
            </label>

            <div className="report-modal__notice">
              Жалоба не удаляет контент автоматически. Модератор проверит материал и примет решение.
            </div>

            <div className="report-modal__footer">
              <button type="button" className="report-btn report-btn--ghost" onClick={closeReport} disabled={submitting}>Отмена</button>
              <button type="submit" className="report-btn report-btn--danger" disabled={submitting}>{submitting ? "Отправляем…" : "Отправить жалобу"}</button>
            </div>
          </form>
        ) : null}
      </div>
    </ReportContentContext.Provider>
  );
}

export function useReportContent() {
  const context = React.useContext(ReportContentContext);
  if (!context) throw new Error("useReportContent must be used inside ReportContentProvider");
  return context;
}
